import React, { useCallback, useEffect, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import classNames from "classnames";
import { useRouter } from "next/router";
import Cookies from "js-cookie";

import { useApp } from "@context/AppProvider";
import { api } from "@utils/api";
import Goodcause from "@components/checkout/blocks/GoodCause";
import Deliverto from "@components/checkout/blocks/deliverTo";
import Loading from "@components/Loading";
import MessageComponent from "@components/common/MessageComponent";
import { cartData, getAge } from "@utils/functions";

import CartAndShipping from "./blocks/CartAndShipping";
import Payment from "./blocks/Payment";
import PromoCode from "./blocks/PromoCode";
import AgeVerify from "./blocks/AgeVerify";

function Checkout(props) {
  const { styles, dispatch, state, session, user, scollToRef } = props;
  const router = useRouter();
  const {
    idb: { currencyData, isoCode },
    fetchSession,
  } = useApp();

  const updateSelectedAddress = useCallback(
    async (val) => {
      if (!val._id) {
        dispatch({
          type: "SET_DATA",
          data: { showShipping: false, addressLoading: true },
        });
        return;
      }
      dispatch({
        type: "SET_DATA",
        data: { mainLoader: true },
      });

      if (val?.countryName?.states) {
        delete val.countryName.states;
      }

      if (!val.countryName && val.Country_Name) {
        val.countryName = val.Country_Name;
      }
      const data = {
        ...val,
        suburb: val.Suburb,
        phonenumber: val.phonenumber,
        country: val.country,
        state: val.state,
      };

      const res = await api(
        {
          url: "/cart/address",
          method: "POST",
          data: {
            address: data,
          },
        },
        session
      );
      let cData = cartData(res);
      dispatch({
        type: "SET_DATA",
        data: { ...cData, addressLoading: false, mainLoader: false },
      });

      if (val.country && val.country !== isoCode) {
        const match = currencyData.find(
          (mp) => mp.isoCode.toLowerCase() === val.country.toLowerCase()
        );
        if (match) {
          Cookies.set("cur", match.currency.toLowerCase());
          Cookies.set("isoCode", match.isoCode);
          fetchSession(true, match.isoCode);
        }
      }
    },
    [dispatch, session, currencyData, isoCode, fetchSession]
  );

  const updateSelectedCharity = useCallback(
    (charity) => {
      dispatch({
        type: "SET_CHARITY",
        data: {
          error: null,
          charityId: charity ? charity._id : null,
        },
      });

      if (charity?._id && user?._id) {
        let obj = {
          charity_id: charity._id,
          userID: user._id,

          is_updated_good_cause: user?.charity?._id ? true : false,
        };
        api(
          {
            url: `/user/updateCharity`,
            method: "POST",
            data: obj,
          },
          session
        );
      }
    },
    [dispatch, session, user?._id, user?.charity?._id]
  );

  const updatePaymentSource = useCallback(
    (card) => {
      dispatch({
        type: "SET_CARD",
        data: {
          error: null,
          paymentSource: card.id,
          card,
        },
      });
    },
    [dispatch]
  );

  const selectedShippings = useMemo(() => {
    let shipping = {};
    for (let i = 0; i < state.shipping.length; i++) {
      const ship = state.shipping[i];
      shipping[ship.retailer_id] = ship.option;
    }
    return shipping;
  }, [state.shipping]);

  const checkAgeValidater = useCallback(() => {
    const currentDate = state.currentDate.split("/");

    const born = new Date(currentDate[2], currentDate[1] - 1, currentDate[0]);
    const age = getAge(born);
    if (age >= 18) {
      dispatch({
        type: "SET_DATA",
        data: { isVerified18Plus: true },
      });
    } else {
      dispatch({
        type: "SET_DATA",
        data: { isVerified18Plus: false },
      });
    }
  }, [dispatch, state.currentDate]);

  useEffect(() => {
    if (state.currentDate) {
      checkAgeValidater();
    }
  }, [checkAgeValidater, state.currentDate]);

  const placeOrder = async () => {
    let error = null;

    if (!state.charityId) {
      error = "Please choose a good cause or opt for selected good cause.";
    }

    if (state.is18Plus && state?.willingToProvideIdentityProof !== true) {
      error =
        "Identity proof needs to be a mandatory if there is alcohol in your order.";
      dispatch({
        type: "SET_DATA",
        data: { isIdentityError: true },
      });
    } else {
      dispatch({
        type: "SET_DATA",
        data: { isIdentityError: null },
      });
    }

    const retailers = {};
    for (let i = 0; i < state.items.length; i++) {
      retailers[state.items[i].retailer_id] = true;
    }

    if (Object.keys(retailers).length !== state.shipping.length) {
      error = "Please select Delivery Options.";
    }

    let productArray = [];
    for (let i = 0; i < state.items.length; i++) {
      const p = state.items[i];
      productArray.push({
        product_id: p.product_id,
        variance_id: p.variance_id,
      });
      const rs = p.product_data.retailer_products.stocks.find(
        (i) => i.variance_id === p.variance_id
      );
      if (p.quantity > rs.stock) {
        error = `Sorry, "${p.name}" is out of stock. Please remove the products before proceeding.`;
      }
    }
    if (state.is18Plus && !state.currentDate) {
      error = `Date of Birth is required`;
    } else if (state.is18Plus && !state.isVerified18Plus) {
      error = `Sorry, it's against the law to sell alcohol products to people under 18 years of age. Please remove any alcohol products that you have in your cart to place your order. `;
    }

    if (error) {
      dispatch({
        type: "SET_ERROR",
        data: {
          orderPlacing: false,
          error: { msg: error },
        },
      });
    } else {
      let retailerId = [];
      const retailerAlochol = state.items.filter(
        (item) => item.product_data?.retailer?.storeDetail?.isChecked === true
      );

      if (retailerAlochol && retailerAlochol.length) {
        retailerId = retailerAlochol.map((item) => item?.retailer_id);
      }

      dispatch({
        type: "SET_ERROR",
        data: {
          orderPlacing: true,
          error: {},
        },
      });
      const res = await api(
        {
          url: "/cart/place-order",
          method: "POST",
          data: {
            goodCause: state.goodCause,
            charityId: state.charityId,
            paymentSource: state.paymentSource,

            stripe_card_country: state.card.country,
            orderType: "buyit",
            ageVerification: {
              isVerified18Plus: state.isVerified18Plus,
              currentDate: state.currentDate,
              retailerId,
            },
            productArray: productArray,
          },
        },
        session
      );
      if (res.status) {
        sessionStorage.removeItem("guestCheckout");
        dispatch({
          type: "SET_ERROR",
          data: {
            orderPlacing: false,
            error: null,
          },
        });
        router.push(`/thankyou/${res.id}`);
      } else {
        if (res.length > 0) {
          dispatch({
            type: "SET_ERROR",
            data: {
              orderPlacing: false,
              error: { msg: res[0].message },
            },
          });
        } else {
          dispatch({
            type: "SET_ERROR",
            data: {
              orderPlacing: false,
              error: { msg: res.message },
            },
          });
        }
      }
    }
  };

  const btnType = () => {
    let status = true;

    const retailers = {};
    for (let i = 0; i < state.items.length; i++) {
      retailers[state.items[i].retailer_id] = true;
    }

    if (Object.keys(retailers).length !== state.shipping.length) {
      status = false;
    }

    if (!state.charityId) {
      status = false;
    }

    if (!state.paymentSource) {
      status = false;
    }

    if (!state.address && !state.address._id) {
      status = false;
    }

    if (state.is18Plus && !state.isVerified18Plus) {
      status = false;
    }

    return status ? styles.placeOrder : styles.placeOrderGray;
  };

  useEffect(() => {
    if (state.address && Object.keys(state.address).length > 0) {
      dispatch({
        type: "SET_DATA",
        data: { showShipping: true },
      });
    } else {
      dispatch({
        type: "SET_DATA",
        data: { showShipping: false },
      });
    }
  }, [dispatch, state.address]);

  const updateAgeData = useCallback(
    (obj) => {
      dispatch({
        type: "SET_DATA",
        data: { ...obj },
      });
    },
    [dispatch]
  );

  const cartAndShipping = useMemo(
    () => (
      <CartAndShipping
        {...state}
        styles={styles}
        dispatch={dispatch}
        scollToRef={scollToRef}
        session={session}
        items={state.items}
        selectedAddress={state.address}
        selectedShippings={selectedShippings}
        cartData={cartData}
        user={user}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.address, state.items]
  );

  return (
    <div className="container">
      <div className="row">
        {state.mainLoader && <Loading />}
        <div className={`col-md-12 col-lg-8 ${styles.mainLeftBlock}`}>
          <div
            className={`row ${styles.mainBagBlock}`}
            ref={(ref) => (scollToRef.current["bag"] = ref)}
          >
            <div className="col-md-12 col-lg-12">{cartAndShipping}</div>
          </div>
          {user && (
            <div
              className={`row ${styles.mainDeliveryBlock}`}
              ref={(ref) => (scollToRef.current["shipping"] = ref)}
            >
              <div className="col-md-12 col-lg-12">
                <Deliverto
                  callBackChangeAddress={updateSelectedAddress}
                  selectedAddress={{
                    ...state.address,
                  }}
                  addressLoading={state.addressLoading ? true : false}
                  session={session}
                  user={user}
                />
              </div>
            </div>
          )}
        </div>
        {user && (
          <div
            className="col-md-12 col-lg-4"
            ref={(ref) => (scollToRef.current["payment"] = ref)}
          >
            <div className={styles.yourCauseBlock}>
              <Goodcause
                goodCause={state.goodCause}
                callBackUpdateCharity={updateSelectedCharity}
              />

              <div className={styles.promoBlock}>
                <PromoCode
                  styles={styles}
                  data={state.promo}
                  session={session}
                  dispatch={dispatch}
                  user={user}
                  error={state.error}
                />
              </div>

              <div className={styles.paymentDetailsBlock}>
                <Payment
                  {...state.total}
                  styles={styles}
                  session={session}
                  user={user}
                  updatePaymentSource={updatePaymentSource}
                />
              </div>
              <div className={styles.msgInfoBlock}>
                All prices displayed on this page are in Australian Dollars
                (AUD) and your card will be charged in this currrency. By
                placing this order you agree and understand you will be charged
                in Australian Dollars{" "}
              </div>

              {state.error &&
                state.error.msg &&
                state.error.msg !==
                  "Identity proof needs to be a mandatory if there is alcohol in your order." && (
                  <MessageComponent
                    message={state.error.msg}
                    messageKey="error"
                  />
                )}

              <AgeVerify
                items={state.items}
                callBack={updateAgeData}
                state={state}
              />
              <button
                className={classNames("btnCustom", btnType())}
                onClick={() => placeOrder()}
                disabled={state.orderPlacing}
              >
                <span className={styles.placeOrderSpan}>
                  <Image
                    width="20"
                    height="20"
                    src={`/assets/images/lock-white.png`}
                    alt="stripe"
                    style={{ objectFit: "contain" }}
                  />
                  Place Order
                </span>
                {state.orderPlacing && <Loading.LoaderThree />}
              </button>
              <div className={styles.placeOrderLinks}>
                <ul>
                  <li>
                    <Link href="/pdf/btc_terms_condition.pdf" target="_blank">
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link href="/privacy-policy">Privacy Policy</Link>
                    <Link href="/pdf/btc_privacy_policy.pdf" target="_blank">
                      (PDF)
                    </Link>
                  </li>
                  <li>
                    <Link href="/returns" target="_blank">
                      Returns
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Checkout;
